import { createSlice } from '@reduxjs/toolkit';
import { DEFAULT_CHECKIN_TIME } from '../constants';
import apolloClient from '../queries';
import { FetchPlan } from '../queries/index.graphql';
import { makePlanInput } from '../utils/input';
import { getFlightState, getHotelState, saveSelectedPlans, getPlanDetailState } from '../utils/localStorage';
import { sessionKey } from './app';
import { history } from '../utils/history';

// type plans = {
//   startDate: string;
//   stayNights: number;
//   plan: object;
//   checkin: string;
// }

const initialState = {
  fetching: false,
  plans: [],
  pendingPlan: undefined,
};


const planDetailSlice = createSlice({
  name: 'planDetail',
  initialState,
  reducers: {
    setFetchingPlanDetail: {
      reducer(state, action) {
        const { fetching } = action.payload;
        state.fetching = fetching;
      },
      prepare(fetching) {
        return { payload: { fetching } };
      }
    },
    setPlans: {
      reducer(state, action) {
        const { plans } = action.payload;
        state.plans = plans;
      },
      prepare(plans) {
        return { payload: { plans } };
      }
    },
    addPlanItem: {
      reducer(state, action) {
        const { planItem } = action.payload;
        const newStartDate = planItem.startDate;
        const filteredPlans = state.plans.filter((p) => p.startDate !== newStartDate);
        state.plans = [...filteredPlans, planItem];

        saveSelectedPlans(sessionKey, state.plans);
      },
      prepare(planItem) {
        return { payload: { planItem } };
      }
    },
    updateCheckin: {
      reducer(state, action) {
        const { checkin, date } = action.payload;
        const newPlans = state.plans.map((p) => {
          if (p.startDate !== date) {
            return p;
          }

          return { ...p, checkin };
        });
        state.plans = newPlans;

        saveSelectedPlans(sessionKey, newPlans);
      },
      prepare(checkin, date) {
        return { payload: { checkin, date } };
      }
    },
    updateQuestion: {
      reducer(state, action) {
        const { title, selected, date } = action.payload;
        const newPlans = state.plans.map((p) => {
          if (p.startDate !== date) {
            return p;
          }

          const { plan } = p;
          const newQuestions = plan.questions.map((q) => {
            if (q.title !== title) {
              return q;
            }

            return { ...q, selected };
          });

          const newPlan = { ...plan, questions: newQuestions };
          return { ...p, plan: newPlan };
        });
        state.plans = newPlans;

        saveSelectedPlans(sessionKey, newPlans);
      },
      prepare(title, selected, date) {
        return { payload: { title, selected, date } };
      }
    },
    removePlan: {
      reducer(state, action) {
        const { startDate } = action.payload;
        const newPlans = state.plans.filter((p) => {
          return p.startDate !== startDate;
        });
        state.plans = newPlans;

        saveSelectedPlans(sessionKey, state.plans);
      },
      prepare(startDate) {
        return { payload: { startDate } };
      }
    },
    setPendingPlan: {
      reducer(state, action) {
        const { plan } = action.payload;
        state.pendingPlan = plan;
      },
      prepare(plan) {
        return { payload: { plan } };
      }
    },
  },
});

export const {
  setFetchingPlanDetail,
  setPlans,
  addPlanItem,
  updateCheckin,
  updateQuestion,
  removePlan,
  setPendingPlan,
} = planDetailSlice.actions;

const fetchPlan = (planInput, startDate, stayNights) => async (dispatch) => {
  dispatch(setFetchingPlanDetail(true));

  try {
    const { data } = await apolloClient.query({
      query: FetchPlan,
      variables: {
        input: planInput,
      },
      fetchPolicy: 'no-cache',
    });

    const {
      plan,
    } = data;

    dispatch(addResultToPlans(plan, startDate, stayNights));

  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);

    //エラーメッセージを表示する
    let messages = [];
    if (error && error.graphQLErrors && error.graphQLErrors.length) {
      messages = error.graphQLErrors.map((e) => (e.debugMessage || e.message)).filter(Boolean);
    }
    history.push({ pathname: '/error', state: { m: messages.join(', ') } });

  } finally {
    dispatch(setFetchingPlanDetail(false));
  }
};

export const fetchPendingPlan = (planInput) => async (dispatch) => {
  dispatch(setFetchingPlanDetail(true));

  try {
    const { data } = await apolloClient.query({
      query: FetchPlan,
      variables: {
        input: planInput,
      },
      fetchPolicy: 'no-cache',
    });

    const {
      plan,
    } = data;

    dispatch(setPendingPlan(plan));

  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  } finally {
    dispatch(setFetchingPlanDetail(false));
  }
};

export const removePendingPlan = () => async (dispatch) => {
  dispatch(setPendingPlan(undefined));
};

const addResultToPlans = (plan, startDate, stayNights) => async (dispatch) => {
  const checkin = plan.checkinInfo.start || DEFAULT_CHECKIN_TIME;
  const validatedQuestions = plan.questions ? plan.questions.map((q) => {
    const { required, selections } = q;
    // if (!required) {
    //   return q;
    // }

    return { ...q, selected: (selections && selections.length) ? selections[0] : null };
  }): [];
  const validatedPlan = { ...plan, questions: validatedQuestions };
  const planItem = { plan: validatedPlan, startDate, stayNights, checkin };
  dispatch(addPlanItem(planItem));
};

export const handleAddPlan = (plan, startDate, stayNights, searchedHotelsInput, searchedFlightsInput) => async (dispatch) => {
  const { id: planId, roomType } = plan;
  const { id: roomTypeId } = roomType;
  const planInput = makePlanInput(planId, roomTypeId, searchedHotelsInput, searchedFlightsInput, startDate);
  dispatch(fetchPlan(planInput, startDate, stayNights));
};

export const handleUpdateCheckin = (checkin, date) => (dispatch) => {
  dispatch(updateCheckin(checkin, date));
};

export const handleUpdateQuestion = (title, selected, date) => (dispatch) => {
  dispatch(updateQuestion(title, selected, date));
};

export const handleDeletePlan = (startDate) => (dispatch) => {
  dispatch(removePlan(startDate));
};

export const handleQueryParamsPlanDetail = (queryParams) => (dispatch) => {
  const { p, rt, k } = queryParams;

  const hotelState = getHotelState(k);

  const { searchedHotelsInput } = hotelState;
  let stayNights = 1;
  if (searchedHotelsInput) {
    stayNights = searchedHotelsInput.stayNights;
  }

  const flightState = getFlightState(k);

  let startDate = undefined;

  const { searchedFlightsInput } = flightState;
  if (searchedFlightsInput) {
    startDate = searchedFlightsInput.outbound.date;
  }

  const planInput = makePlanInput(p, rt, searchedHotelsInput, searchedFlightsInput);

  dispatch(fetchPlan(planInput, startDate, stayNights));
};

export const handleQueryParamsEntryConfirm = (queryParams) => (dispatch) => {
  const { p, rt, k } = queryParams;
  const planDetailState = getPlanDetailState(k);

  if (planDetailState.plans) {
    dispatch(setPlans(planDetailState.plans));
  } else {
    const hotelState = getHotelState(k);

    const { searchedHotelsInput } = hotelState;
    let stayNights = 1;
    if (searchedHotelsInput) {
      stayNights = searchedHotelsInput.stayNights;
    }

    const flightState = getFlightState(k);

    let startDate = undefined;

    const { searchedFlightsInput } = flightState;
    if (searchedFlightsInput) {
      startDate = searchedFlightsInput.outbound.date;
    }

    const planInput = makePlanInput(p, rt, searchedHotelsInput, searchedFlightsInput);

    dispatch(fetchPlan(planInput, startDate, stayNights));
  }
};

export default planDetailSlice.reducer;
